import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { sendApiRequest } from '../utils/api';
import { useAuth } from '../context/AuthContext';

import LOGO from '../assets/logo.jpeg';
import Illustration from '../assets/login-illustration.jpeg';

const Login = () => {
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();
  const { login } = useAuth();

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError('');

      const response = await sendApiRequest(
        'api/auth/login',
        {
          email: data.email,
          password: data.password
        },
        'POST'
      );

      if (!response || response.status === false) {
        throw new Error(response?.message || 'Login failed');
      }

      if (!response?.data?.token) {
        throw new Error('Invalid server response');
      }

      localStorage.setItem('auth_token', response.data.token);
      login(response.data.user);

      if (response.data.user?.role === 'admin') {
        navigate('/admin-panel');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#EAF0FF] to-[#DDE6FF] px-4 py-8 font-[montserrat]">
      <div className="w-full max-w-6xl bg-white rounded-3xl shadow-2xl overflow-hidden">

        <div className="grid grid-cols-1 md:grid-cols-2">

          {/* ================= LEFT PANEL ================= */}
          <div className="hidden md:flex relative flex-col items-center justify-center bg-[#F5F7FF] p-6 lg:p-10">
            <img
              src={LOGO}
              alt="Logo"
              className="h-28 lg:h-36 mb-6"
            />

            <img
              src={Illustration}
              alt="Login Illustration"
              className="max-w-xs lg:max-w-sm"
            />

            <div className="absolute bottom-0 left-0 w-full bg-gradient-to-r from-[#2F5DA8] to-[#4B7BE5] text-white text-center py-3 text-sm lg:text-base font-semibold">
              By. Parihar Sir, Private Secretary High Court
            </div>
          </div>

          {/* ================= RIGHT PANEL ================= */}
          <div className="flex flex-col justify-center p-6 sm:p-8 md:p-12 bg-[#F4F7FB] relative">

            <h1 className="text-xl sm:text-2xl font-semibold mb-1 text-[#2F5DA8] text-center md:text-left">
              Welcome to Shorthand Software
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 mb-6 sm:mb-8 text-center md:text-left">
              Please login to your account
            </p>

            {/* ERROR */}
            {error && (
              <div className="mb-4 border border-red-300 bg-red-50 text-red-600 px-4 py-2 rounded text-xs sm:text-sm">
                {error}
              </div>
            )}

            {/* FORM */}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-5">

              {/* EMAIL */}
              <div>
                <input
                  type="text"
                  placeholder="Enter your Email"
                  {...register('email', { required: 'Email is required' })}
                  className="w-full px-4 py-2.5 sm:py-3 border border-gray-300 rounded-md focus:outline-none focus:border-[#2F5DA8]"
                />
                {errors.email && (
                  <p className="text-xs text-red-500 mt-1">
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* PASSWORD */}
              <div>
                <input
                  type="password"
                  placeholder="Enter your Password"
                  {...register('password', { required: 'Password is required' })}
                  className="w-full px-4 py-2.5 sm:py-3 border border-gray-300 rounded-md focus:outline-none focus:border-[#2F5DA8]"
                />
                {errors.password && (
                  <p className="text-xs text-red-500 mt-1">
                    {errors.password.message}
                  </p>
                )}
              </div>

              {/* BUTTON */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#2F5DA8] to-[#4B7BE5] text-white py-2.5 sm:py-3 rounded-md font-medium hover:opacity-90 transition disabled:opacity-60"
              >
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </form>

            {/* REGISTER */}
            <p className="text-center text-xs sm:text-sm text-gray-600 mt-6">
              Not registered?{' '}
              <Link
                to="/register"
                className="text-[#2F5DA8] font-medium hover:underline"
              >
                Create Account
              </Link>
            </p>

            {/* MOBILE FOOTER STRIP */}
            <div className="mt-4 md:hidden flex justify-center">
              <p className="px-4 py-2 rounded-lg text-xs font-bold bg-gradient-to-r from-[#2F5DA8] to-[#4B7BE5] text-white text-center">
                By. Parihar Sir, Private Secretary High Court
              </p>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
